import { ChangeEvent } from 'react';

import { WorkspaceSwitcherCreateParamsKind } from '@gearbelt/global';
import { Control, FieldValues, Path, PathValue } from 'react-hook-form';
import { SingleValue } from 'react-select';
import { z } from 'zod';

import { cn } from '@/lib/utils';
import { accountService } from '@/services/global-api/account.service';

import ArrowDownSmallIcon from '../icons/arrow-down-small';
import { FormFieldItem, FormFieldItemWithTooltip } from '../ui/form';
import { Input } from '../ui/input';
import { Select } from '../ui/select';

export const CreateWorkspaceAsRegularUserSchema = z.object({
  workspaceName: z.string().min(1, { message: 'Required' }),
});
export type CreateWorkspaceAsRegularUserSchemaType = z.infer<typeof CreateWorkspaceAsRegularUserSchema>;

export const CreateWorkspaceWithExistingAccountSchema = z.object({
  kind: z.literal(WorkspaceSwitcherCreateParamsKind.UseExistingAccount),
  workspaceName: CreateWorkspaceAsRegularUserSchema.shape.workspaceName,
  accountId: z.string().min(1, { message: 'Required' }),
  accountName: z.string().optional(),
  // Workaround. RHF has a limitation around discriminated unions as it'll always treat the new fields as an uncontrolled input.
  accountSubdomain: z.string().optional(),
});
export const CreateWorkspaceWithNewAccountSchema = z.object({
  kind: z.literal(WorkspaceSwitcherCreateParamsKind.CreateNewAccount),
  workspaceName: CreateWorkspaceAsRegularUserSchema.shape.workspaceName,
  accountName: z.string().min(1, { message: 'Required' }),
  accountRegion: z.string().min(1, { message: 'Required' }),
  accountSubdomain: z
    .string()
    .min(1, { message: 'Required' })
    .max(50, { message: 'Must not exceed 50 characters' })
    .superRefine(async (val, ctx) => {
      const validSubdomainRegex = /^[a-z0-9]+(-[a-z0-9]+)*$/;

      if (val.includes(' ')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Spaces are not allowed',
        });
      } else if (!val.match(/^[a-zA-Z0-9- ]*$/)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Special characters are not allowed',
        });
      } else if (val.match(/[A-Z]/)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Uppercase characters are not allowed',
        });
      } else if (!val.match(validSubdomainRegex)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Domains must not start/end with hyphens, or contain consecutive hyphens.',
        });
      } else {
        await accountService.validateSubdomain(val).catch((error) => {
          const errorResponseData = error.response.data.error;
          if (errorResponseData?.subdomainExists) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Must be unique',
            });
          } else {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Invalid',
            });
          }
        });
      }
    }),
});
export const CreateWorkspaceSchema = z.discriminatedUnion('kind', [CreateWorkspaceWithExistingAccountSchema, CreateWorkspaceWithNewAccountSchema]);
export type CreateWorkspaceSchemaType = z.infer<typeof CreateWorkspaceSchema>;

export function CreateWorkspaceSelectField<T>({
  label,
  name,
  control,
  options,
  required,
  onChange: onChangeOverride,
  labelSize,
  disabled,
  readonly,
}: {
  label: string;
  name: Path<CreateWorkspaceSchemaType>;
  control: Control<CreateWorkspaceSchemaType>;
  options: { label: string; value: T }[];
  required?: boolean;
  onChange?: (value: T | undefined) => void;
  labelSize?: 'base' | 'small';
  disabled?: boolean;
  readonly?: boolean;
}) {
  return (
    <FormFieldItem<CreateWorkspaceSchemaType, Path<CreateWorkspaceSchemaType>>
      control={control}
      name={name}
      label={label}
      required={required}
      labelSize={labelSize}
      hideRequiredLabel>
      {({ value, onChange, error }) => (
        <Select<{ label: string; value: T }>
          value={options.find((opt) => opt.value === value)}
          onChange={(val: SingleValue<{ label: string; value: T }>) => {
            if (onChangeOverride) {
              onChangeOverride(val?.value);
            } else {
              onChange(val?.value as PathValue<CreateWorkspaceSchemaType, Path<CreateWorkspaceSchemaType>>);
            }
          }}
          options={options}
          className={cn('disabled:bg-grey-1 w-full')}
          styles={{
            control: (styles) => ({
              ...styles,
              borderColor: error ? 'rgb(var(--default-red))' : styles.borderColor,
              backgroundColor: readonly ? 'rgb(var(--grey-1))' : styles.backgroundColor,
            }),
          }}
          components={{
            DropdownIndicator: () => <ArrowDownSmallIcon />,
          }}
          blurInputOnSelect
          isDisabled={disabled || readonly}
        />
      )}
    </FormFieldItem>
  );
}

export function CreateWorkspaceInputField<TFieldValues extends FieldValues>({
  label,
  name,
  control,
  placeholder,
  required,
  disabled,
  className,
  labelSize,
  readonly,
  noErrorMessage,
}: {
  label: string;
  name: Path<TFieldValues>;
  control: Control<TFieldValues>;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  labelSize?: 'base' | 'small';
  readonly?: boolean;
  noErrorMessage?: boolean;
}) {
  return (
    <FormFieldItem<TFieldValues, Path<TFieldValues>>
      control={control}
      name={name}
      label={label}
      required={required}
      labelSize={labelSize}
      className={className}
      noErrorMessage={noErrorMessage}
      hideRequiredLabel>
      {({ value, onChange, error }) => (
        <Input
          className={cn('disabled:bg-grey-1', className)}
          placeholder={placeholder ?? `Enter ${label}...`}
          value={value as string}
          onChange={onChange as unknown as (e: ChangeEvent<HTMLInputElement>) => void}
          error={!!error && !noErrorMessage}
          disabled={disabled || readonly}
        />
      )}
    </FormFieldItem>
  );
}

export function CreateWorkspaceInputFieldWithTooltip({
  label,
  name,
  control,
  placeholder,
  required,
  disabled,
  className,
  labelSize,
  readonly,
  noErrorMessage,
  tooltipContent,
  tooltipContentProps,
}: {
  label: string;
  name: Path<CreateWorkspaceSchemaType>;
  control: Control<CreateWorkspaceSchemaType>;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  labelSize?: 'base' | 'small';
  readonly?: boolean;
  noErrorMessage?: boolean;
  tooltipContent: React.ReactNode;
  tooltipContentProps?: Parameters<typeof FormFieldItemWithTooltip>[0]['tooltipContentProps'];
}) {
  return (
    <FormFieldItemWithTooltip<CreateWorkspaceSchemaType, Path<CreateWorkspaceSchemaType>>
      control={control}
      name={name}
      label={label}
      required={required}
      labelSize={labelSize}
      className={className}
      noErrorMessage={noErrorMessage}
      hideRequiredLabel
      tooltipContent={tooltipContent}
      tooltipContentProps={tooltipContentProps}>
      {({ value, onChange, error }) => (
        <Input
          className={cn('disabled:bg-grey-1', className)}
          placeholder={placeholder ?? `Enter ${label}...`}
          value={value as string}
          onChange={onChange as unknown as (e: ChangeEvent<HTMLInputElement>) => void}
          error={!!error && !noErrorMessage}
          disabled={disabled || readonly}
        />
      )}
    </FormFieldItemWithTooltip>
  );
}

export function WorkspaceNameInputField<TFieldValues extends FieldValues>({
  name,
  control,
  required,
  disabled,
  className,
  labelSize,
  readonly,
}: {
  name: Path<TFieldValues>;
  control: Control<TFieldValues>;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  labelSize?: 'base' | 'small';
  readonly?: boolean;
}) {
  return (
    <CreateWorkspaceInputField<TFieldValues>
      label="Workspace Name"
      name={name}
      control={control}
      required={required}
      labelSize={labelSize}
      className={className}
      disabled={disabled || readonly}
    />
  );
}

export function AccountSubdomainInputField({
  name,
  control,
  required,
  disabled,
  className,
  labelSize,
  readonly,
}: {
  name: Path<CreateWorkspaceSchemaType>;
  control: Control<CreateWorkspaceSchemaType>;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  labelSize?: 'base' | 'small';
  readonly?: boolean;
}) {
  return (
    <FormFieldItem<CreateWorkspaceSchemaType, Path<CreateWorkspaceSchemaType>>
      control={control}
      name={name}
      label="Sitemate Start Account Subdomain"
      required={required}
      labelSize={labelSize}
      className={className}
      hideRequiredLabel>
      {({ value, onChange, error }) => (
        <div className="flex flex-row items-center gap-1">
          <Input
            className={cn('disabled:bg-grey-1', className)}
            placeholder={'Text...'}
            value={value as string}
            onChange={onChange as unknown as (e: ChangeEvent<HTMLInputElement>) => void}
            error={!!error}
            disabled={disabled || readonly}
          />
          <span className="text-default-text text-xs">.sitematestart.com</span>
        </div>
      )}
    </FormFieldItem>
  );
}
